import React, { createContext, useContext, useState, useEffect } from "react";
import { useChat } from "../hooks/useChat";
import { ChatItem, Source, Thread } from "../types/types";
import { useLocation } from "react-router-dom";

interface ChatContextProps {
  threads: Thread[];
  changeThread: (thread: Thread | null, fetch?: boolean) => void;
  deleteThread: (payload: { id: string; title: string }) => Promise<void>;
  renameThread: (payload: { id: string; oldTitle: string; newTitle: string }) => Promise<void>;
  selectedThread: Thread | null;
  sendQuery: (payload: { query?: string; [key: string]: any }) => void;
  abortQuery: () => void;
  isLoadingQuery: boolean;
  isLoadingSelectedThread: boolean;
  activeStreamThreadIds: string[];
  chatItems: ChatItem[];
  fetchMessages: (threadId: string) => void;
  feedbackEndpoint: string;
  selectedSource: Source | null;
  changeSelectedSource: (source: Source | null) => void;
  setSelectedSource: (source: Source | null) => void;
  setFilters: (filters: any) => void;
  clearChat: () => void;
  query: string;
  setQuery: (query: string) => void;
  isReformulation: boolean;
  setIsReformulation: (value: boolean) => void;
  resumeExhaustiveSearchStream: (threadId: string) => Promise<void>;
  appendOptimisticUserMessage: (text: string, threadId: string) => void;
  removeOptimisticUserMessages: () => void;
  appendOptimisticThoughtMessage: (text: string, threadId: string) => void;
  upsertOptimisticThoughtMessage: (text: string, threadId: string) => void;
  removeOptimisticThoughtMessages: () => void;
  /** Stop SSE + message système + suppression du partiel (bouton croix). */
  interruptStreamForThread: (threadId: string) => void;
  /** Dev : POST SSE — macro finale seule, à partir des panneaux carrousel déjà en base. */
  resynthesizeExhaustiveFinal: (
    threadId: string,
    page: string
  ) => Promise<void>;
}

const ChatContext = createContext<ChatContextProps | undefined>(undefined);

export const ChatProvider: React.FC<{
  children: React.ReactNode;
  threadsEndpoint: string;
  messagesEndpoint: string;
  chatEndpoint: string;
  feedbackEndpoint: string;
  exhaustiveSearchResumeEndpoint?: string;
}> = ({
  children,
  threadsEndpoint,
  messagesEndpoint,
  chatEndpoint,
  feedbackEndpoint,
  exhaustiveSearchResumeEndpoint,
}) => {
  const [selectedSource, setSelectedSource] = useState<Source | null>(null);
  const [query, setQuery] = useState<string>("");
  const [isReformulation, setIsReformulation] = useState<boolean>(false);
  const location = useLocation();

  const chat = useChat(
    threadsEndpoint,
    messagesEndpoint,
    chatEndpoint,
    {
      setQuery,
      setIsReformulation,
    },
    exhaustiveSearchResumeEndpoint
  );

  useEffect(() => {
    // Navigation hors conversation/page: arrêter le stream en cours.
    chat.abortQuery();
    setSelectedSource(null);
    setIsReformulation(false);
    setQuery("");
  }, [location.pathname]);

  return (
    <ChatContext.Provider
      value={{
        ...chat,
        feedbackEndpoint,
        selectedSource,
        setSelectedSource,
        changeSelectedSource: setSelectedSource,
        clearChat: chat.clearChat,
        query,
        setQuery,
        isReformulation,
        setIsReformulation,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};
